import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { createPlaylistUrls, keyLookup, videoTimeLookup, videoTimestampExtractor } from "components/util/PlaylistUtils";
import { convertToTimezone } from "components/util/TimezoneUtils";
import videojs from "video.js";
import { Parser } from "m3u8-parser";
import { useSelector } from "react-redux";
import _ from "lodash";

const csrfTokenSelector = (state) => state.csrfToken;
const userConfigSelector = (state) => state.userDetails.userConfig;

const createPlaylistFromVideoKeys = (videoSource, videoKeys, discontinuityIndexes) => {
    if (!videoKeys) {
        return;
    }

    const playlist = [
        "#EXTM3U",
        "#EXT-X-PLAYLIST-TYPE:VOD",
        "#EXT-X-TARGETDURATION:1.0",
        "#EXT-X-VERSION:4",
        "#EXT-X-MEDIA-SEQUENCE:0",
        "#EXT-X-DISCONTINUITY",
    ];

    // Could probably move this to the backend eventually. Leaving here for backward compatibility
    const basePlaylistURL = videoSource.split("index")[0];

    videoKeys.forEach((keyData, index) => {
        const keyParts = keyData[0].split(".");
        if (keyParts[1] === "c0" || discontinuityIndexes.includes(index)) {
            playlist.push("#EXT-X-DISCONTINUITY");
        }
        playlist.push(`#EXTINF:${keyData[2]}`);
        playlist.push(`${basePlaylistURL}${keyData[0]}.ts`);
    });
    playlist.push("#EXT-X-ENDLIST");

    const playlistString = playlist.join("\n");
    var parser = new Parser();
    parser.push(playlistString);
    parser.end();
    return parser;
};

const StandAloneVideoPlayer = ({ selectedSession, playlist, setVideoTimestamp, currentPlayerPosition }) => {
    const videoRef = useRef(null);
    const playerRef = useRef(null);
    const csrfTokenRef = useRef(null);
    const inFlightAsyncRequests = useRef({});

    const csrfToken = useSelector(csrfTokenSelector);

    const userConfig = useSelector(userConfigSelector);

    const [timeCorrection] = useState(0);
    // const [sourceIndex, setSourceIndex] = useState(0);
    // const [isEnhanced, setIsEnhanced] = useState(false);
    const sourceIndex = 0;
    const isEnhanced = false;

    const video = useMemo(() => {
        if (playlist && playlist.video_playlists) {
            return playlist.video_playlists[sourceIndex];
        }
        return [];
    }, [playlist, sourceIndex]);

    useEffect(() => {
        csrfTokenRef.current = csrfToken;

        return () => {
            csrfTokenRef.current = null;
        };
    }, [csrfToken]);

    const playlistUrls = useMemo(() => {
        if (!selectedSession) {
            return {};
        }
        const playlistUrls = createPlaylistUrls(selectedSession, sourceIndex);
        return playlistUrls;
    }, [sourceIndex, selectedSession]);

    const videoSource = useMemo(() => {
        let source = null;
        if (!playlistUrls) return source;

        if (isEnhanced) {
            source = playlistUrls.enhanced;
        } else if (isEnhanced === "low_res") {
            source = playlistUrls.lowRes;
        } else {
            source = playlistUrls.raw;
        }

        return source;
    }, [isEnhanced, playlistUrls]);

    const formatTime = useCallback(
        (seconds) => {
            try {
                if (video) {
                    const corrected = seconds - timeCorrection;
                    const playlistIndex = videoTimeLookup(corrected, video);

                    if (playlistIndex !== -1) {
                        const playlistItem = video[playlistIndex];
                        const timeOffset = corrected - playlistItem[1];

                        if (videoTimestampExtractor(playlistItem)) {
                            const displayTime = videoTimestampExtractor(playlistItem) + timeOffset;
                            const dpDate = new Date(displayTime * 1000);
                            return convertToTimezone(dpDate, userConfig.convert_to_utc);
                        }
                    }
                }
            } catch (ex) {
                console.log("Unexpected exception while formatting time!", ex);
            }
            return "";
        },
        [video, userConfig, timeCorrection],
    );

    const generateAsyncVideoRequest = useCallback(
        (indexToRequest, basePlaylistURL) => {
            const sidx = `${indexToRequest}`;
            const inFlight = inFlightAsyncRequests.current;

            // Skip if already requested
            if (_.has(inFlight, sidx) || _.isUndefined(csrfTokenRef.current)) return;

            const videoKey = video[indexToRequest][0];
            const videoURI = `${basePlaylistURL}${videoKey}.ts`;

            const controller = new AbortController();
            inFlight[sidx] = controller;

            fetch(videoURI, {
                method: "GET",
                mode: "cors",
                credentials: "omit",
                signal: controller.signal,
                headers: {
                    "X-AIVR-Security-Token": csrfTokenRef.current,
                },
            })
                .catch((error) => {
                    // silent catch for aborts or network errors
                })
                .finally(() => {
                    delete inFlight[sidx];
                });
        },
        [video],
    );

    const asyncPreloadVideo = useCallback(
        (playlistIndex) => {
            // Clean up old in-flight fetches
            _.forEach(inFlightAsyncRequests.current, (controller, sidx) => {
                const idx = Number(sidx);
                if (idx < playlistIndex - 5 || idx > playlistIndex + 15) {
                    controller.abort();
                    delete inFlightAsyncRequests.current[sidx];
                }
            });

            const basePlaylistURL = videoSource.split("index")[0];

            // Preload next 10 segments
            let indexToRequest = playlistIndex;
            while (indexToRequest < Math.min(video.length, playlistIndex + 10)) {
                generateAsyncVideoRequest(indexToRequest, basePlaylistURL);
                indexToRequest += 1;
            }
        },
        [videoSource, video.length, generateAsyncVideoRequest],
    );

    // Register custom format time once
    useEffect(() => {
        videojs.setFormatTime(formatTime);
    }, [formatTime]);

    // Initialize + destroy video.js player
    useEffect(() => {
        if (videoRef.current && !playerRef.current) {
            // Load video source
            if (videoSource) {
                const parser = createPlaylistFromVideoKeys(videoSource, video, []);
                if (!parser) {
                    return;
                }

                playerRef.current = videojs(videoRef.current, {
                    controls: true,
                    autoplay: false,
                    preload: "auto",
                    playbackRates: [0.1, 0.2, 0.5, 1],
                    html5: {
                        hls: {
                            overrideNative: true,
                        },
                        nativeVideoTracks: false,
                        nativeAudioTracks: false,
                        nativeTextTracks: false,
                    },
                    controlBar: {
                        volumePanel: false,
                        fullscreenToggle: false,
                        pictureInPictureToggle: false,
                    },
                    userActions: {
                        doubleClick: false,
                    },
                    inactivityTimeout: 0,
                });

                playerRef.current.src({
                    src: `data:application/vnd.videojs.vhs+json,${JSON.stringify(parser.manifest)}`,
                    type: "application/vnd.videojs.vhs+json",
                });

                // --- HLS/VHS Customization ---
                if (videojs.Hls) {
                    videojs.Hls.MAX_GOAL_BUFFER_LENGTH = 10;
                    videojs.Hls.GOAL_BUFFER_LENGTH = 10;
                }

                if (videojs.Vhs && videojs.Vhs.xhr) {
                    videojs.Vhs.xhr.beforeRequest = (options) => {
                        if (!options.headers) options.headers = {};

                        if (csrfTokenRef.current) {
                            options.headers["X-AIVR-Security-Token"] = csrfTokenRef.current;
                        }

                        const videoKey = options.uri?.replace(/.*\/([^/]*)\.ts/, "$1");

                        const startingIndex = keyLookup(videoKey, video);
                        if (startingIndex === -1) {
                            console.warn("no matching key for:", videoKey, video);
                            return options;
                        }

                        asyncPreloadVideo(startingIndex); // now a stable callback

                        return options;
                    };
                }

                playerRef.current.ready(() => {
                    playerRef.current.playbackRate(0.5);
                    playerRef.current.seekButtons({
                        forward: 1,
                        back: 1,
                    });
                    playerRef.current.on("timeupdate", () => {
                        setVideoTimestamp(playerRef.current.currentTime());
                    });

                    if (currentPlayerPosition) {
                        playerRef.current.currentTime(currentPlayerPosition);
                    }
                });
            }
        }
    }, [asyncPreloadVideo, currentPlayerPosition, setVideoTimestamp, video, videoSource]);

    return (
        <div className="video-player">
            <video
                ref={videoRef}
                className="video-js vjs-default-skin"
                playsInline
                style={{ width: "100%", height: "auto" }}
            />
        </div>
    );
};

export default React.memo(StandAloneVideoPlayer);
